import RBush from 'rbush'
import type { BBox } from 'rbush'
import { Capacitor } from '@capacitor/core'
import { logger } from '../../utils/logger.js'
import { CARRIERS } from './carrier-roaming-map.js'
import type { Technology } from './cellular-coverage-service.js'

const log = logger.create('cellular-tower-db')

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const DATA_URL = `${TILE_ORIGIN}/data/cellular/cellular-towers.bin`
const IDB_NAME = 'cellular-tower-cache'
const IDB_STORE = 'binary'
const IDB_KEY = 'towers-v1'

const MAGIC = 0x4c4c4543 // "CELL" little-endian
const HEADER_SIZE = 10
const RADIO_NAMES: Technology[] = ['GSM', 'LTE', 'NR', 'UMTS', 'CDMA']

const MCC_MNC_TO_CARRIER: Map<number, string> = new Map()
const CARRIER_ENTRIES: [string, string, [number, number][]][] = [
  [
    'AT&T',
    'att',
    [
      [310, 410],
      [310, 412],
      [310, 380],
    ],
  ],
  [
    'T-Mobile',
    'tmobile',
    [
      [310, 260],
      [310, 200],
      [310, 210],
      [310, 220],
      [310, 230],
      [310, 240],
      [310, 250],
      [310, 270],
      [310, 310],
      [310, 490],
      [310, 660],
      [310, 800],
    ],
  ],
  [
    'Verizon',
    'verizon',
    [
      [311, 480],
      [311, 481],
      [311, 482],
      [311, 483],
      [311, 484],
      [311, 485],
      [311, 486],
      [311, 487],
      [311, 488],
      [311, 489],
      [310, 4],
      [310, 10],
      [310, 12],
      [310, 13],
    ],
  ],
  [
    'US Cellular',
    'uscellular',
    [
      [311, 580],
      [311, 581],
      [311, 582],
      [311, 583],
      [311, 584],
      [311, 585],
      [311, 586],
      [311, 587],
      [311, 588],
      [311, 589],
    ],
  ],
  ['FirstNet', 'firstnet', [[313, 100]]],
]

for (const [, carrierId, pairs] of CARRIER_ENTRIES) {
  for (const [mcc, mnc] of pairs) {
    MCC_MNC_TO_CARRIER.set(mcc * 10000 + mnc, carrierId)
  }
}

export interface TowerRecord {
  index: number
  radio: Technology
  mcc: number
  net: number
  area: number
  cell: number
  unit: number
  lon: number
  lat: number
  range: number
  signal: number
  carrierId: string
}

interface TowerBBox extends BBox {
  idx: number
}

class CellularTowerDb {
  private tree: RBush<TowerBBox> | null = null
  private radioCol: Uint8Array | null = null
  private mccCol: Uint16Array | null = null
  private netCol: Uint16Array | null = null
  private areaCol: Uint32Array | null = null
  private cellLoCol: Uint32Array | null = null
  private cellHiCol: Uint32Array | null = null
  private unitCol: Uint16Array | null = null
  private lonCol: Float32Array | null = null
  private latCol: Float32Array | null = null
  private rangeCol: Uint16Array | null = null
  private signalCol: Uint8Array | null = null
  private carrierIds: Uint8Array | null = null
  private count = 0
  private loading: Promise<void> | null = null

  get ready(): boolean {
    return this.tree !== null
  }

  get towerCount(): number {
    return this.count
  }

  async load(): Promise<void> {
    if (this.tree) return
    if (this.loading) return this.loading
    this.loading = this.doLoad()
    return this.loading
  }

  private async doLoad(): Promise<void> {
    const t0 = performance.now()
    let buffer = await this.loadFromIdb()

    if (!buffer) {
      log.info('Downloading tower data...')
      const resp = await fetch(DATA_URL)
      if (!resp.ok) throw new Error(`Failed to fetch tower data: ${resp.status}`)
      buffer = await resp.arrayBuffer()
      void this.saveToIdb(buffer)
    }

    this.parse(buffer)
    this.buildIndex()
    log.info(
      `Loaded ${this.count.toLocaleString()} towers in ${(performance.now() - t0).toFixed(0)}ms`
    )
  }

  private parse(buffer: ArrayBuffer): void {
    const view = new DataView(buffer)
    const magic = view.getUint32(0, true)
    if (magic !== MAGIC) throw new Error('Invalid tower binary: bad magic')

    const version = view.getUint8(4)
    if (version !== 1) throw new Error(`Unsupported tower binary version: ${version}`)

    this.count = view.getUint32(5, true)
    const n = this.count

    // Columns may not be aligned for typed array views, so we use DataView reads
    // and copy into properly aligned typed arrays
    let offset = HEADER_SIZE

    this.radioCol = new Uint8Array(buffer, offset, n)
    offset += n

    this.mccCol = new Uint16Array(n)
    for (let i = 0; i < n; i++) {
      this.mccCol[i] = view.getUint16(offset + i * 2, true)
    }
    offset += n * 2

    this.netCol = new Uint16Array(n)
    for (let i = 0; i < n; i++) {
      this.netCol[i] = view.getUint16(offset + i * 2, true)
    }
    offset += n * 2

    this.areaCol = new Uint32Array(n)
    for (let i = 0; i < n; i++) {
      this.areaCol[i] = view.getUint32(offset + i * 4, true)
    }
    offset += n * 4

    this.cellLoCol = new Uint32Array(n)
    this.cellHiCol = new Uint32Array(n)
    for (let i = 0; i < n; i++) {
      this.cellLoCol[i] = view.getUint32(offset + i * 8, true)
      this.cellHiCol[i] = view.getUint32(offset + i * 8 + 4, true)
    }
    offset += n * 8

    this.unitCol = new Uint16Array(n)
    for (let i = 0; i < n; i++) {
      this.unitCol[i] = view.getUint16(offset + i * 2, true)
    }
    offset += n * 2

    this.lonCol = new Float32Array(n)
    for (let i = 0; i < n; i++) {
      this.lonCol[i] = view.getFloat32(offset + i * 4, true)
    }
    offset += n * 4

    this.latCol = new Float32Array(n)
    for (let i = 0; i < n; i++) {
      this.latCol[i] = view.getFloat32(offset + i * 4, true)
    }
    offset += n * 4

    this.rangeCol = new Uint16Array(n)
    for (let i = 0; i < n; i++) {
      this.rangeCol[i] = view.getUint16(offset + i * 2, true)
    }
    offset += n * 2

    this.signalCol = new Uint8Array(buffer, offset, n)

    // Pre-compute carrier index per tower
    this.carrierIds = new Uint8Array(n)
    const carrierIdList = CARRIERS.map(c => c.id)
    for (let i = 0; i < n; i++) {
      const key = this.mccCol[i] * 10000 + this.netCol[i]
      const cid = MCC_MNC_TO_CARRIER.get(key)
      const cidx = cid ? carrierIdList.indexOf(cid) : 255
      this.carrierIds[i] = cidx !== -1 ? cidx : 255
    }
  }

  private buildIndex(): void {
    const n = this.count
    const lon = this.lonCol!
    const lat = this.latCol!
    const range = this.rangeCol!

    const items: TowerBBox[] = new Array(n)
    for (let i = 0; i < n; i++) {
      const rangeDegLat = range[i] / 111000
      const rangeDegLon = range[i] / (111000 * Math.cos((lat[i] * Math.PI) / 180))
      items[i] = {
        minX: lon[i] - rangeDegLon,
        minY: lat[i] - rangeDegLat,
        maxX: lon[i] + rangeDegLon,
        maxY: lat[i] + rangeDegLat,
        idx: i,
      }
    }

    this.tree = new RBush<TowerBBox>()
    this.tree.load(items)
  }

  queryTowersInExtent(
    bounds: { west: number; south: number; east: number; north: number },
    carrierId?: string,
    enabledTechnologies?: Set<Technology>
  ): TowerRecord[] {
    if (!this.tree) return []

    const results = this.tree.search({
      minX: bounds.west,
      minY: bounds.south,
      maxX: bounds.east,
      maxY: bounds.north,
    })

    const carrierIdList = CARRIERS.map(c => c.id)
    const carrierIdx = carrierId ? carrierIdList.indexOf(carrierId) : -1

    const towers: TowerRecord[] = []
    for (const item of results) {
      const i = item.idx

      if (carrierIdx >= 0 && this.carrierIds![i] !== carrierIdx) continue

      const radio = RADIO_NAMES[this.radioCol![i]]
      if (enabledTechnologies && !enabledTechnologies.has(radio)) continue

      towers.push({
        index: i,
        radio,
        mcc: this.mccCol![i],
        net: this.netCol![i],
        area: this.areaCol![i],
        cell: this.cellLoCol![i] + this.cellHiCol![i] * 0x100000000,
        unit: this.unitCol![i],
        lon: this.lonCol![i],
        lat: this.latCol![i],
        range: this.rangeCol![i],
        signal: this.signalCol![i],
        carrierId: carrierIdList[this.carrierIds![i]] || 'unknown',
      })
    }

    return towers
  }

  queryTowersForDisplay(
    bounds: { west: number; south: number; east: number; north: number },
    enabledCarriers: Set<string>,
    enabledTechnologies: Set<Technology>
  ): TowerRecord[] {
    if (!this.tree) return []

    const results = this.tree.search({
      minX: bounds.west,
      minY: bounds.south,
      maxX: bounds.east,
      maxY: bounds.north,
    })

    const carrierIdList = CARRIERS.map(c => c.id)
    const towers: TowerRecord[] = []

    for (const item of results) {
      const i = item.idx
      const cid = carrierIdList[this.carrierIds![i]]
      if (!cid || !enabledCarriers.has(cid)) continue

      const radio = RADIO_NAMES[this.radioCol![i]]
      if (!enabledTechnologies.has(radio)) continue

      // Only include if the tower center is within the visible bounds (for display, not LOS)
      const lon = this.lonCol![i]
      const lat = this.latCol![i]
      if (lon < bounds.west || lon > bounds.east || lat < bounds.south || lat > bounds.north)
        continue

      towers.push({
        index: i,
        radio,
        mcc: this.mccCol![i],
        net: this.netCol![i],
        area: this.areaCol![i],
        cell: this.cellLoCol![i] + this.cellHiCol![i] * 0x100000000,
        unit: this.unitCol![i],
        lon,
        lat,
        range: this.rangeCol![i],
        signal: this.signalCol![i],
        carrierId: cid,
      })
    }

    return towers
  }

  private async loadFromIdb(): Promise<ArrayBuffer | null> {
    try {
      const db = await this.openIdb()
      return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly')
        const req = tx.objectStore(IDB_STORE).get(IDB_KEY)
        req.onsuccess = () => resolve(req.result ?? null)
        req.onerror = () => reject(req.error ?? new Error('IDB read failed'))
        tx.oncomplete = () => db.close()
      })
    } catch {
      return null
    }
  }

  private async saveToIdb(buffer: ArrayBuffer): Promise<void> {
    try {
      const db = await this.openIdb()
      const tx = db.transaction(IDB_STORE, 'readwrite')
      tx.objectStore(IDB_STORE).put(buffer, IDB_KEY)
      tx.oncomplete = () => db.close()
    } catch (e) {
      log.warn('Failed to cache tower data in IDB:', e)
    }
  }

  private openIdb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1)
      req.onupgradeneeded = () => {
        req.result.createObjectStore(IDB_STORE)
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error ?? new Error('IDB open failed'))
    })
  }
}

export const cellularTowerDb = new CellularTowerDb()
